import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BASE_URL } from './BaseUrl';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InnerHeader from './InnerHeader';
import { GridToolbar } from '@mui/x-data-grid';
import Loader from './Loader';
import { StyledDataGrid } from './StyledDataGrid';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { getRoleData } from '../Store/Role/role-action';

const CACHE_KEY = 'course_data'; // Key for localStorage caching

const CourseListing = () => {
    const [coursedata, setCourseData] = useState([]);
    const [cid, setCid] = useState("");
    const [confirmationVisibleMap, setConfirmationVisibleMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 50,
        page: 0,
    });

    async function getCourseData() {
        axios.get(`${BASE_URL}/getCourse`)
            .then((res) => {
                const active = (Array.isArray(res.data) ? res.data : []).filter(
                    (c) => c && c.IsDelete != 1 && c.isDelete != 1 && c.deleted != 1
                );
                localStorage.setItem(CACHE_KEY, JSON.stringify({
                    data: active,
                    timestamp: Date.now()
                }));
                setCourseData(active);
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    }

    useEffect(() => {
        getCourseData();
    }, []);

    // Check if course is referenced in any other table across the system
    const checkCourseUsage = async (courseId) => {
        const usages = [];
        const cIdStr = String(courseId);
        try {
            const [
                batchesRes,
                studentsRes,
                admissionsRes,
                inquiriesRes,
                feesRes,
                vivaRes,
                finalRes,
                batchLeftRes,
                batchCancelRes
            ] = await Promise.all([
                axios.post(`${BASE_URL}/get_new_data`, { tablename: 'Batch_Mst', columnname: 'Batch_Id, Course_Id' }).catch(() => ({ data: [] })),
                axios.post(`${BASE_URL}/get_new_data`, { tablename: 'Student_Master', columnname: 'Student_Id, Course_Id' }).catch(() => ({ data: [] })),
                axios.post(`${BASE_URL}/get_new_data`, { tablename: 'Admission_master', columnname: 'Admission_Id, Course_Id' }).catch(() => ({ data: [] })),
                axios.post(`${BASE_URL}/get_new_data`, { tablename: 'Student_Inquiry', columnname: 'Inquiry_Id, Course_Id' }).catch(() => ({ data: [] })),
                axios.post(`${BASE_URL}/get_new_data`, { tablename: 'S_Fees_Mst', columnname: 'Fees_Id, Course_Id' }).catch(() => ({ data: [] })),
                axios.post(`${BASE_URL}/get_new_data`, { tablename: 'viva_taken', columnname: 'Take_Id, Course_Id' }).catch(() => ({ data: [] })),
                axios.post(`${BASE_URL}/get_new_data`, { tablename: 'generate_final_result', columnname: 'Id, Course_Id' }).catch(() => ({ data: [] })),
                axios.post(`${BASE_URL}/get_data`, { tablename: 'awt_batchleft' }).catch(() => ({ data: [] })),
                axios.post(`${BASE_URL}/get_data`, { tablename: 'awt_batchcancellation' }).catch(() => ({ data: [] }))
            ]);

            if (Array.isArray(batchesRes.data) && batchesRes.data.some(b => String(b.Course_Id) === cIdStr)) {
                usages.push("Batches");
            }
            if (Array.isArray(studentsRes.data) && studentsRes.data.some(s => String(s.Course_Id) === cIdStr)) {
                usages.push("Students");
            }
            if (Array.isArray(admissionsRes.data) && admissionsRes.data.some(a => String(a.Course_Id) === cIdStr)) {
                usages.push("Admissions");
            }
            if (Array.isArray(inquiriesRes.data) && inquiriesRes.data.some(i => String(i.Course_Id) === cIdStr)) {
                usages.push("Inquiries");
            }
            if (Array.isArray(feesRes.data) && feesRes.data.some(f => String(f.Course_Id) === cIdStr)) {
                usages.push("Fees");
            }
            if (Array.isArray(vivaRes.data) && vivaRes.data.some(v => String(v.Course_Id) === cIdStr)) {
                usages.push("Viva Taken");
            }
            if (Array.isArray(finalRes.data) && finalRes.data.some(g => String(g.Course_Id) === cIdStr)) {
                usages.push("Final Results");
            }
            if (Array.isArray(batchLeftRes.data) && batchLeftRes.data.some(bl => String(bl.course) === cIdStr)) {
                usages.push("Batch Left");
            }
            if (Array.isArray(batchCancelRes.data) && batchCancelRes.data.some(bc => String(bc.course) === cIdStr)) {
                usages.push("Batch Cancellation");
            }
        } catch (err) {
            console.error("Error checking course usage:", err);
        }
        return usages;
    };

    const handleClick = async (id) => {
        const courseObj = coursedata.find(c => c.Course_Id === id);
        const courseName = courseObj?.Course_Name || `Course #${id}`;

        const usages = await checkCourseUsage(id);
        if (usages.length > 0) {
            alert(`Cannot delete "${courseName}" because it is currently in use in: ${usages.join(", ")}.\n\nA course cannot be deleted while it is linked to other records.`);
            return;
        }

        setCid(id);
        setConfirmationVisibleMap((prevMap) => ({
            ...prevMap,
            [id]: true,
        }));
    };

    const handleCancel = (id) => {
        setConfirmationVisibleMap((prevMap) => ({
            ...prevMap,
            [id]: false,
        }));
    };

    const handleDelete = async (id) => {
        const courseObj = coursedata.find(c => c.Course_Id === id);
        const courseName = courseObj?.Course_Name || `Course #${id}`;

        const usages = await checkCourseUsage(id);
        if (usages.length > 0) {
            alert(`Cannot delete "${courseName}" because it is currently in use in: ${usages.join(", ")}.`);
            setConfirmationVisibleMap((prevMap) => ({
                ...prevMap,
                [id]: false,
            }));
            return;
        }

        const data = {
            delete_id: id,
            tablename: "Course_Mst",
            column_name: 'Course_Id'
        };

        axios.post(`${BASE_URL}/new_delete_data`, data)
            .then((res) => {
                if (res.data && res.data.inUse) {
                    alert(res.data.message || "Cannot delete course: in use elsewhere.");
                    return;
                }
                alert("Course deleted successfully!");
                getCourseData();
            })
            .catch((err) => {
                console.log(err);
                alert("Failed to delete course.");
            });

        setConfirmationVisibleMap((prevMap) => ({
            ...prevMap,
            [id]: false,
        }));
    };

    const roledata = {
        role: Cookies.get(`role`),
        pageid: 13
    };

    const dispatch = useDispatch();
    const roleaccess = useSelector((state) => state.roleAssign?.roleAssign[0]?.accessid);

    useEffect(() => {
        dispatch(getRoleData(roledata));
    }, []);

    const columns = [
        {
            field: 'index',
            headerName: 'Sr. No.',
            type: 'number',
            align: 'center',
            headerAlign: 'center',
            flex: 0.5,
            filterable: false,
        },
        { field: 'Course_Name', headerName: 'Course Name', flex: 2 },
        { field: 'Course_Code', headerName: 'Course Code', flex: 2 },
        { field: 'Introduction', headerName: 'Introduction', flex: 2 },
        ...(roleaccess > 2 ? [{
            field: 'actions',
            type: 'actions',
            headerName: 'Action',
            flex: 0.5,
            renderCell: (params) => {
                return (
                    <>
                        {roleaccess > 2 && (
                            <Link to={`/course/${params.row.Course_Id}`}>
                                <EditIcon style={{ cursor: "pointer", marginRight: "8px" }} />
                            </Link>
                        )}
                        {roleaccess > 3 && (
                            <DeleteIcon
                                style={{ color: "red", cursor: "pointer" }}
                                onClick={() => handleClick(params.row.Course_Id)}
                            />
                        )}
                    </>
                );
            }
        }] : [])
    ];

    const rowsWithIds = coursedata.map((row, index) => ({ index: index + 1, ...row }));

    return (
        <div className="container-fluid page-body-wrapper ">
            <InnerHeader />

            {loading && <Loader />}

            <div className="main-panel" style={{ display: loading ? "none" : "block" }}>
                <div className="content-wrapper">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className='d-flex justify-content-between' style={{ width: "100%", padding: "10px 0", borderBottom: "2px solid #dce4ec" }}>
                                        <div>
                                            <h4 className="card-title">List of Course ({coursedata.length})</h4>
                                        </div>
                                        {roleaccess > 1 && (
                                            <Link to='/course/:courseid'>
                                                <button className='btn btn-success'>Add +</button>
                                            </Link>
                                        )}
                                    </div>

                                    <div style={{ borderLeft: "1px solid #dce4ec", height: "510px", overflow: "hidden" }}>
                                        <StyledDataGrid
                                            rows={rowsWithIds}
                                            columns={columns}
                                            disableColumnSelector
                                            disableDensitySelector
                                            rowHeight={35}
                                            getRowId={(row) => row.Course_Id}
                                            pagination
                                            paginationModel={paginationModel}
                                            onPaginationModelChange={setPaginationModel}
                                            pageSizeOptions={[50]}
                                            autoHeight={false}
                                            sx={{
                                                height: 500,
                                                '& .MuiDataGrid-footerContainer': {
                                                    justifyContent: 'flex-end',
                                                },
                                            }}
                                            slots={{
                                                toolbar: GridToolbar
                                            }}
                                            slotProps={{
                                                toolbar: {
                                                    showQuickFilter: true,
                                                },
                                            }}
                                        />

                                        {confirmationVisibleMap[cid] && (
                                            <div className='confirm-delete'>
                                                <p>Are you sure you want to delete?</p>
                                                <button onClick={() => handleDelete(cid)} className='btn btn-sm btn-primary'>OK</button>
                                                <button onClick={() => handleCancel(cid)} className='btn btn-sm btn-danger'>Cancel</button>
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseListing;